import React, {
  useRef,
  useCallback,
  useState,
  useMemo,
  useEffect,
  LegacyRef,
} from "react";
import { useIntl } from "react-intl";
import clsx from "clsx";
import BaseLinkOrButton from "./BaseLinkOrButton";
import useMediaQuery from "./useMediaQuery";
import { useCurrentLocaleDirection } from "../utils/locale";
import useResizeObserver from "./useResizeObserver";

interface SwimlaneProps {
  children: React.ReactNode;
  className?: string;
  scrollMargin?: number;
  styleVariant?: "row";
}

export default function Swimlane({
  children,
  className,
  scrollMargin = 0,
  styleVariant,
}: SwimlaneProps) {
  const intl = useIntl();
  const swimlaneRef = useRef<HTMLDivElement>(null);
  const localeDirection = useCurrentLocaleDirection();
  const isLtr = useMemo(() => localeDirection === "ltr", [localeDirection]);
  const [canScrollForward, setCanScrollForward] = useState<boolean>(true);
  const [canScrollBack, setCanScrollBack] = useState<boolean>(false);
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );
  const { ref: listRef, rect } = useResizeObserver();

  const needsScrollButtons = useMemo(() => {
    if (listRef.current && rect) {
      return listRef.current.scrollWidth > rect.borderBoxSize[0].inlineSize;
    }
  }, [rect, listRef]);

  // abstracts offset irrespective of direction
  const getInlineOffset = useCallback(
    (li: HTMLLIElement, w: number) => {
      return isLtr
        ? li.offsetLeft
        : Math.abs(li.offsetLeft + li.offsetWidth - w);
    },
    [isLtr]
  );

  // note tries to keep whole list items in scroll view rather than
  // seeing one half of one item and then the other half of it after scroll
  // optional scrollMargin so items aren't flush with scroll container, looks nicer

  const scrollForward = useCallback(() => {
    if (!listRef.current) {
      return;
    }
    const lis = Array.from(listRef.current.querySelectorAll("li"));
    const availableWidth = swimlaneRef.current?.clientWidth ?? 0;
    const scrollOffset = Math.abs(listRef.current.scrollLeft);

    const nextLi = lis.find((li) => {
      const inlineOffset = getInlineOffset(li, availableWidth);
      return (
        inlineOffset - scrollOffset + li.offsetWidth > availableWidth &&
        inlineOffset - scrollOffset > 0 + scrollMargin
      );
    });
    if (nextLi) {
      const dir = isLtr ? 1 : -1;
      const last = lis.indexOf(nextLi) === lis.length - 1;
      const x =
        nextLi.offsetLeft + (!isLtr ? nextLi.offsetWidth - availableWidth : 0);
      listRef.current.scrollTo({
        left: last
          ? (listRef.current.scrollWidth - availableWidth) * dir
          : x - scrollMargin * dir,
        behavior: prefersReducedMotion ? "instant" : "smooth",
      });
    }
  }, [getInlineOffset, isLtr, listRef, prefersReducedMotion, scrollMargin]);

  const scrollBack = useCallback(() => {
    if (!listRef.current) {
      return;
    }
    const lisInitial = Array.from(listRef.current.querySelectorAll("li"));
    const lis = [...lisInitial].reverse();
    const availableWidth = swimlaneRef.current?.clientWidth ?? 0;
    const scrollOffset = Math.abs(listRef.current.scrollLeft);
    const prevLi = lis.find((li) => {
      const inlineOffset = getInlineOffset(li, availableWidth);
      return (
        inlineOffset - scrollOffset < 0 &&
        inlineOffset - scrollOffset + li.offsetWidth + scrollMargin <
          availableWidth
      );
    });
    if (prevLi) {
      const dir = isLtr ? 1 : -1;
      const x =
        prevLi.offsetLeft + (isLtr ? prevLi.offsetWidth - availableWidth : 0);
      listRef.current.scrollTo({
        left: lisInitial.indexOf(prevLi) === 0 ? 0 : x + scrollMargin * dir,
        behavior: prefersReducedMotion ? "instant" : "smooth",
      });
    }
  }, [getInlineOffset, isLtr, listRef, prefersReducedMotion, scrollMargin]);

  const handleScrollOrResizeEvent = useCallback(() => {
    if (listRef.current && swimlaneRef.current) {
      const scrollLeft = isLtr
        ? listRef.current.scrollLeft
        : listRef.current.scrollLeft * -1;
      if (!canScrollBack && scrollLeft > 0) {
        setCanScrollBack(true);
      }
      if (!listRef.current.scrollLeft) {
        setCanScrollBack(false);
      }

      const fullyScrolledWidth = scrollLeft + swimlaneRef.current?.clientWidth;
      if (
        !canScrollForward &&
        fullyScrolledWidth < listRef.current.scrollWidth
      ) {
        setCanScrollForward(true);
      }

      if (listRef.current.scrollWidth - fullyScrolledWidth < 1) {
        setCanScrollForward(false);
      }
    }
  }, [
    isLtr,
    canScrollForward,
    setCanScrollForward,
    canScrollBack,
    setCanScrollBack,
    listRef,
    swimlaneRef,
  ]);

  useEffect(() => {
    handleScrollOrResizeEvent();
  }, [rect, handleScrollOrResizeEvent]);

  return (
    <div
      className={clsx(
        "vst-swimlane",
        styleVariant === "row" && "vst-swimlane-variant-row",
        className
      )}
      ref={swimlaneRef}
    >
      {needsScrollButtons && (
        <>
          {canScrollBack && (
            <BaseLinkOrButton
              aria-label={intl.formatMessage({ defaultMessage: "Scroll Back" })}
              data-tooltip-content={intl.formatMessage({
                defaultMessage: "Scroll Back",
              })}
              onClick={scrollBack}
              className="vst-swimlane-back-button"
            />
          )}
          {canScrollForward && (
            <BaseLinkOrButton
              aria-label={intl.formatMessage({
                defaultMessage: "Scroll Forward",
              })}
              data-tooltip-content={intl.formatMessage({
                defaultMessage: "Scroll Forward",
              })}
              onClick={scrollForward}
              className="vst-swimlane-forward-button"
            />
          )}
        </>
      )}
      <ul
        ref={listRef as LegacyRef<HTMLUListElement>}
        onScroll={handleScrollOrResizeEvent}
      >
        {children}
      </ul>
    </div>
  );
}
